import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { marked } from 'marked';
import { Sparkles, Send, X, Loader2, Settings2, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  'What is leaking the most money right now?',
  'Which channel should I move budget into and why?',
  'Explain my blended ROAS from the numbers on screen.',
  'What is the single biggest blind spot this month?',
];

export const IntelligencePanel = ({ open, onClose, context, sessionId, T, isDark, F }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [live, setLive] = useState('');
  const [config, setConfig] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/intelligence/config`, { withCredentials: true })
      .then(r => setConfig(r.data)).catch(() => setConfig(null));
    axios.get(`${API}/api/intelligence/history?session_id=${sessionId}`, { withCredentials: true })
      .then(r => setMessages(r.data.messages || [])).catch(() => setMessages([]));
  }, [open, sessionId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, live]);

  const ask = useCallback(async (text) => {
    const q = (text || question).trim();
    if (!q || streaming) return;
    setQuestion('');
    setMessages(m => [...m, { role: 'user', content: q }]);
    setStreaming(true);
    setLive('');
    let answer = '';
    try {
      const resp = await fetch(`${API}/api/intelligence/ask`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, session_id: sessionId, context }),
      });
      if (!resp.ok || !resp.body) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(typeof body.detail === 'string' ? body.detail : 'The analyst is unavailable.');
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith('data:')) continue;
          const evt = JSON.parse(part.slice(5));
          if (evt.delta) { answer += evt.delta; setLive(answer); }
          if (evt.error) { answer = answer || `**${evt.error}**`; setLive(answer); }
        }
      }
      setMessages(m => [...m, { role: 'assistant', content: answer }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', content: `**${e.message}**` }]);
    } finally {
      setStreaming(false);
      setLive('');
    }
  }, [question, streaming, sessionId, context]);

  const clear = async () => {
    await axios.delete(`${API}/api/intelligence/history?session_id=${sessionId}`, { withCredentials: true });
    setMessages([]);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div data-testid="intelligence-panel" style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)' }} />
      <aside style={{ position: 'relative', width: 'min(520px, 100%)', height: '100%', background: T.bg,
        borderLeft: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column' }}>
        <header style={{ padding: '16px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <Sparkles size={16} style={{ color: '#00D4FF' }} />
              <h2 style={{ fontSize: 15, fontWeight: 700, fontFamily: F.head, color: T.text }}>Ask OmniAgent</h2>
            </div>
            <p data-testid="intel-model-line" style={{ fontSize: 11, fontFamily: F.mono, color: T.muted, marginTop: 3 }}>
              {config ? `${config.provider} · ${config.model}${config.has_key ? ' · your key' : ''}` : 'loading model…'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button data-testid="intel-settings" onClick={() => navigate('/settings/intelligence')} title="Model settings"
              style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${T.border}`, background: T.surfaceAlpha, color: T.muted, cursor: 'pointer' }}>
              <Settings2 size={14} />
            </button>
            <button data-testid="intel-clear" onClick={clear} title="Clear conversation"
              style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${T.border}`, background: T.surfaceAlpha, color: T.muted, cursor: 'pointer' }}>
              <Trash2 size={14} />
            </button>
            <button data-testid="intel-close" onClick={onClose} title="Close"
              style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${T.border}`, background: T.surfaceAlpha, color: T.muted, cursor: 'pointer' }}>
              <X size={14} />
            </button>
          </div>
        </header>

        <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
          {!messages.length && !live && (
            <div data-testid="intel-empty">
              <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.7, marginBottom: 14 }}>
                Grounded in the figures currently on this dashboard for <strong style={{ color: T.text }}>{context?.brand?.name}</strong>,
                {' '}{context?.range}. It will refuse to answer anything the connected data cannot support.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {SUGGESTIONS.map(s => (
                  <button key={s} data-testid="intel-suggestion" onClick={() => ask(s)}
                    style={{ textAlign: 'left', padding: '9px 12px', borderRadius: 7, border: `1px solid ${T.border}`,
                      background: T.surfaceAlpha, color: T.text, fontSize: 12.5, cursor: 'pointer', fontFamily: F.body }}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} data-testid={`intel-msg-${m.role}`} style={{ marginBottom: 16 }}>
              {m.role === 'user' ? (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <p style={{ maxWidth: '85%', padding: '9px 13px', borderRadius: 12, fontSize: 13,
                    background: 'rgba(0,47,167,.12)', color: T.text, fontFamily: F.body }}>{m.content}</p>
                </div>
              ) : (
                <div className={`blog-prose ${isDark ? 'blog-prose-dark' : ''}`} style={{ fontSize: 13.5, color: T.text }}
                  dangerouslySetInnerHTML={{ __html: marked.parse(m.content || '') }} />
              )}
            </div>
          ))}

          {live && (
            <div data-testid="intel-streaming" className={`blog-prose ${isDark ? 'blog-prose-dark' : ''}`}
              style={{ fontSize: 13.5, color: T.text }} dangerouslySetInnerHTML={{ __html: marked.parse(live) }} />
          )}
          {streaming && !live && (
            <p style={{ fontSize: 12, color: T.muted, display: 'flex', alignItems: 'center', gap: 6, fontFamily: F.mono }}>
              <Loader2 size={13} className="animate-spin" /> reading your numbers…
            </p>
          )}
          <div ref={endRef} />
        </div>

        <form onSubmit={e => { e.preventDefault(); ask(); }}
          style={{ padding: 14, borderTop: `1px solid ${T.border}`, display: 'flex', gap: 8 }}>
          <input data-testid="intel-input" value={question} onChange={e => setQuestion(e.target.value)}
            placeholder="Ask about the numbers on screen…" aria-label="Ask about your numbers"
            style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: `1px solid ${T.border}`,
              background: T.surfaceAlpha, color: T.text, fontSize: 13, fontFamily: F.body, outline: 'none' }} />
          <button data-testid="intel-send" type="submit" disabled={streaming || !question.trim()}
            style={{ width: 42, borderRadius: 8, border: 'none', cursor: streaming ? 'not-allowed' : 'pointer',
              background: 'linear-gradient(135deg,#002FA7,#00D4FF)', color: '#fff', opacity: streaming || !question.trim() ? 0.5 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {streaming ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          </button>
        </form>
      </aside>
    </div>
  );
};

export default IntelligencePanel;
